/**
 * UI 渲染模块 - 全新仪表盘风格
 */

import type {
  AnalysisResult,
  DeviceInfo,
  ReliabilityLadder,
  ConfidenceResult,
  HistogramData,
} from '../types';

// 格式化工具（带边界检查）
const fmt = {
  num: (n: number, d = 1) => isFinite(n) ? n.toFixed(d) : '--',
  int: (n: number) => isFinite(n) ? Math.round(n).toLocaleString() : '--',
  hex: (n: number) => isFinite(n) ? n.toString(16).padStart(4, '0').toUpperCase() : '----',
  pct: (n: number) => isFinite(n) ? `${n.toFixed(1)}%` : '--%',
};

// DOM缓存
const domCache = new Map<string, HTMLElement>();
function $(id: string): HTMLElement | null {
  if (!domCache.has(id)) {
    const el = document.getElementById(id);
    if (el) domCache.set(id, el);
  }
  return domCache.get(id) || null;
}

export class UIRenderer {
  /**
   * 初始化应用
   */
  initializeApp(): void {
    const app = document.getElementById('app');
    if (!app) return;

    app.innerHTML = `
      <div class="app">
        <!-- 顶栏 -->
        <header class="topbar">
          <div class="logo">
            <div class="logo-icon">P</div>
            <div class="logo-info">
              <span class="logo-text">轮询率测试</span>
              <span class="logo-credit">基于 Marius Heier 的项目开发</span>
            </div>
          </div>
          <div class="topbar-actions">
            <div class="status-pill" id="statusPill" data-status="idle">
              <span id="statusText">等待连接</span>
            </div>
            <button class="btn btn-primary" id="connectBtn">连接设备</button>
            <button class="btn btn-danger hidden" id="disconnectBtn">断开</button>
          </div>
        </header>

        <!-- 主内容 -->
        <div class="main-content">
          <!-- 左侧边栏 -->
          <aside class="sidebar" id="sidebar">
            <div class="sidebar-section" id="deviceSection">
              <div class="sidebar-title">设备信息</div>
              <div id="deviceInfo" class="device-info" style="display:none;"></div>
              <p style="color: var(--text-3); font-size: 13px;" id="noDeviceText">
                请先连接USB设备
              </p>
            </div>

            <div class="sidebar-section" id="actionSection" style="display:none;">
              <div class="sidebar-title">操作</div>
              <div class="action-buttons">
                <button class="btn btn-primary" id="startBtn">开始测试</button>
                <button class="btn" id="exportBtn" disabled>导出数据</button>
              </div>
            </div>

            <div class="sidebar-section history-section" id="historySection" style="display:none;">
              <div class="sidebar-title">测试记录</div>
              <div id="historyContent"></div>
            </div>
          </aside>

          <!-- 右侧结果区 -->
          <main class="result-area" id="resultArea">
            <!-- 欢迎页 -->
            <div class="welcome" id="welcomeView">
              <div class="welcome-icon">🎮</div>
              <h2>USB 轮询率测试</h2>
              <p>连接你的手柄，并不断旋转摇杆，测试它们每秒向电脑发送数据的次数</p>
            </div>

            <!-- 测试中 -->
            <div class="testing-view hidden" id="testingView">
              <div class="pulse-ring"></div>
              <div class="testing-stats">
                <div class="testing-count" id="sampleCount">0</div>
                <div class="testing-label">已采集数据</div>
                <div class="testing-rate" id="realtimeRate">-- Hz</div>
                <div class="testing-label">实时轮询率</div>
              </div>
            </div>

            <!-- 结果页 -->
            <div class="result-view hidden" id="resultView"></div>
          </main>
        </div>
      </div>
    `;

    domCache.clear();
  }

  /**
   * 更新状态
   */
  updateStatus(status: string, text: string): void {
    const pill = $('statusPill');
    const textEl = $('statusText');
    if (pill) pill.setAttribute('data-status', status);
    if (textEl) textEl.textContent = text;
  }

  /**
   * 更新按钮
   */
  updateButtons(connected: boolean, testing: boolean): void {
    const connectBtn = $('connectBtn');
    const disconnectBtn = $('disconnectBtn');
    const startBtn = $('startBtn');
    const exportBtn = $('exportBtn');

    if (connectBtn) connectBtn.classList.toggle('hidden', connected);
    if (disconnectBtn) disconnectBtn.classList.toggle('hidden', !connected);
    if (startBtn) (startBtn as HTMLButtonElement).disabled = testing;
    if (exportBtn) (exportBtn as HTMLButtonElement).disabled = testing;
  }

  /**
   * 显示设备信息
   */
  showDeviceCard(device: DeviceInfo): void {
    const info = $('deviceInfo');
    const noDevice = $('noDeviceText');
    const actionSection = $('actionSection');

    if (noDevice) noDevice.style.display = 'none';
    if (actionSection) actionSection.style.display = 'block';

    if (info) {
      info.style.display = 'block';
      info.innerHTML = `
        <div class="device-name">${device.name}</div>
        <div class="device-meta">
          <div class="device-meta-row">
            <span class="device-meta-label">VID:PID</span>
            <span class="device-meta-value">${fmt.hex(device.vendorId)}:${fmt.hex(device.productId)}</span>
          </div>
          <div class="device-meta-row">
            <span class="device-meta-label">类型</span>
            <span class="device-meta-value">${this.getDeviceTypeName(device.type)}</span>
          </div>
        </div>
      `;
    }
  }

  private getDeviceTypeName(type: string): string {
    const names: Record<string, string> = {
      gamepad: '手柄',
      hid: 'HID设备',
    };
    return names[type] || 'HID设备';
  }

  /**
   * 显示测试动画
   */
  showCaptureAnimation(): void {
    $('welcomeView')?.classList.add('hidden');
    $('resultView')?.classList.add('hidden');
    $('testingView')?.classList.remove('hidden');
  }

  /**
   * 更新采集进度
   */
  updateCaptureProgress(count: number, rate: number): void {
    const countEl = $('sampleCount');
    const rateEl = $('realtimeRate');
    if (countEl) countEl.textContent = fmt.int(count);
    if (rateEl) {
      rateEl.textContent = rate > 0 ? `${fmt.int(Math.round(rate))} Hz` : '-- Hz';
    }
  }

  /**
   * 隐藏测试动画
   */
  hideCaptureAnimation(): void {
    $('testingView')?.classList.add('hidden');
  }

  /**
   * 渲染分析结果
   */
  renderAnalysisResult(result: AnalysisResult): void {
    const view = $('resultView');
    if (!view) return;

    const { metrics, deviceSpeed, histogram } = result;

    view.innerHTML = `
      <!-- 核心指标 -->
      <div class="hero-gauge">
        <div class="hero-number">${fmt.int(Math.round(metrics.effectiveRate))}<span class="hero-unit">Hz</span></div>
        <div class="hero-subtitle">你的设备每秒向电脑发送 ${fmt.int(Math.round(metrics.effectiveRate))} 次数据</div>
      </div>

      <!-- 快速概览 -->
      <div class="quick-stats">
        <div class="stat-box">
          <div class="stat-value">${fmt.num(metrics.efficiency, 1)}%</div>
          <div class="stat-label">性能发挥</div>
          <div class="stat-hint">相比理论最大值</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">±${fmt.num(metrics.timing.stability, 0)}μs</div>
          <div class="stat-label">时间波动</div>
          <div class="stat-hint">越小越稳定</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">${fmt.num(metrics.timing.withinNormal, 1)}%</div>
          <div class="stat-label">正常比例</div>
          <div class="stat-hint">没有延迟的数据占比</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">${fmt.int(result.captureInfo.sampleCount)}</div>
          <div class="stat-label">采集数量</div>
          <div class="stat-hint">测试期间收到的数据包</div>
        </div>
      </div>

      <!-- 提示 -->
      ${this.renderTips(metrics, deviceSpeed)}

      <!-- 详细数据 -->
      <div class="details-section">
        <button class="details-toggle" id="detailsToggle">
          <span>查看详细数据</span>
          <span class="details-toggle-icon">▼</span>
        </button>
        <div class="details-content" id="detailsContent">
          ${this.renderDetailedData(result)}
          ${this.renderCharts(histogram, metrics)}
        </div>
      </div>
    `;

    view.classList.remove('hidden');

    // 绑定折叠切换（直接获取新元素，不用缓存）
    const toggle = document.getElementById('detailsToggle');
    const content = document.getElementById('detailsContent');
    if (toggle && content) {
      toggle.addEventListener('click', () => {
        toggle.classList.toggle('open');
        content.classList.toggle('show');
      });
    }
  }

  private renderTips(metrics: AnalysisResult['metrics'], deviceSpeed: AnalysisResult['deviceSpeed']): string {
    const tips: string[] = [];

    if (deviceSpeed.speedClass === 'HighSpeed' && metrics.effectiveRate > 4000) {
      tips.push(`<strong>高速设备提示：</strong>你的设备是 ${fmt.int(Math.round(metrics.effectiveRate))}Hz 高速设备，浏览器测量精度有限，结果可能有小幅误差`);
    }

    if (metrics.timing.spikeRate > 5) {
      tips.push(`<strong>偶发延迟：</strong>有 ${fmt.num(metrics.timing.spikeRate, 1)}% 的数据出现了延迟，可能是系统后台程序干扰`);
    }

    if (metrics.confidence.score < 70) {
      tips.push(`<strong>测量受限：</strong>本次测量可信度 ${fmt.num(metrics.confidence.score, 0)}%，建议关闭后台程序后重试`);
    }

    if (tips.length === 0) return '';

    return tips.map(tip => `
      <div class="tip-banner">
        <span class="tip-icon">💡</span>
        <div class="tip-content">${tip}</div>
      </div>
    `).join('');
  }

  private renderDetailedData(result: AnalysisResult): string {
    const { metrics, deviceSpeed } = result;

    return `
      <div class="data-grid">
        <div class="data-item">
          <div class="data-label">USB速度类型</div>
          <div class="data-value">${this.getSpeedName(deviceSpeed.speedClass)}</div>
          <div class="data-desc">设备连接的USB速度等级</div>
        </div>
        <div class="data-item">
          <div class="data-label">理论最大值</div>
          <div class="data-value">${fmt.int(deviceSpeed.maxPossibleRate)} Hz</div>
          <div class="data-desc">这种USB类型能达到的最高轮询率</div>
        </div>
        <div class="data-item">
          <div class="data-label">平均间隔</div>
          <div class="data-value">${fmt.num(metrics.timing.mean, 0)} μs</div>
          <div class="data-desc">两次数据之间平均等待多久</div>
        </div>
        <div class="data-item">
          <div class="data-label">中间值</div>
          <div class="data-value">${fmt.num(metrics.timing.median, 0)} μs</div>
          <div class="data-desc">大部分数据的间隔时间</div>
        </div>
        <div class="data-item">
          <div class="data-label">最快 / 最慢</div>
          <div class="data-value">${fmt.num(metrics.timing.median + metrics.timing.jitterRange.min, 0)} / ${fmt.num(metrics.timing.median + metrics.timing.jitterRange.max, 0)} μs</div>
          <div class="data-desc">实际间隔的最小值和最大值</div>
        </div>
        <div class="data-item">
          <div class="data-label">延迟数据占比</div>
          <div class="data-value">${fmt.num(metrics.timing.spikeRate, 2)}%</div>
          <div class="data-desc">出现异常延迟的数据比例</div>
        </div>
      </div>
    `;
  }

  private getSpeedName(speedClass: string): string {
    const names: Record<string, string> = {
      LowSpeed: '低速 (125Hz)',
      FullSpeed: '全速 (1000Hz)',
      HighSpeed: '高速 (8000Hz)',
    };
    return names[speedClass] || speedClass;
  }

  private renderCharts(histogram: HistogramData, metrics: AnalysisResult['metrics']): string {
    return `
      <!-- 间隔分布图 -->
      <div class="chart-section">
        <div class="chart-title">时间间隔分布</div>
        ${this.renderHistogram(histogram)}
      </div>

      <!-- 可靠性 -->
      <div class="chart-section">
        <div class="chart-title">响应速度一致性</div>
        ${this.renderReliability(metrics.reliability)}
      </div>

      <!-- 置信度 -->
      <div class="chart-section">
        <div class="chart-title">测量可信度</div>
        ${this.renderConfidence(metrics.confidence)}
      </div>
    `;
  }

  private renderHistogram(data: HistogramData): string {
    const counts = data.bins.map(b => b.count);
    const maxCount = counts.length > 0 ? Math.max(1, ...counts) : 1;
    const bars = data.bins
      .filter(b => b.count > 0)
      .map(b => {
        const h = (b.count / maxCount) * 100;
        const cls = b.isQuantized ? 'histogram-bar warning' : 'histogram-bar';
        return `<div class="${cls}" style="height:${h}%" title="${fmt.num(b.start,0)}-${fmt.num(b.end,0)}μs: ${b.count}个"></div>`;
      })
      .join('');

    return `
      <div class="histogram-bars">${bars}</div>
      <div class="histogram-axis">
        <span>${fmt.num(data.min, 0)}μs</span>
        <span>${fmt.num(data.max, 0)}μs</span>
      </div>
      ${data.outlierCount > 0 ? `<div style="font-size:11px;color:var(--yellow);margin-top:8px;">⚠️ ${data.outlierCount}个极端值未显示</div>` : ''}
    `;
  }

  private renderReliability(data: ReliabilityLadder): string {
    const p = data.percentiles;
    const nominal = p.p50 > 0 ? p.p50 : 1;  // 防止除以零
    const maxVal = p.p999 > 0 ? p.p999 : 1;

    // 计算一致性评价
    const ratio = nominal > 0 ? p.p99 / nominal : 1;
    let verdict: string;
    if (ratio <= 1.5) {
      verdict = '非常一致，几乎每次响应速度都一样';
    } else if (ratio <= 2) {
      verdict = '比较一致，偶尔有轻微波动';
    } else if (ratio <= 5) {
      verdict = '有一定波动，少数情况会慢一些';
    } else {
      verdict = '波动较大，偶尔会明显卡顿';
    }

    const rungs = [
      { label: '1/2', value: p.p50, desc: '一半的响应比这快' },
      { label: '1/100', value: p.p99, desc: '只有1%比这慢' },
      { label: '1/1000', value: p.p999, desc: '只有0.1%比这慢' },
    ];

    const getBarClass = (value: number) => {
      const r = value / nominal;
      if (r > 5) return 'ladder-fill danger';
      if (r > 2) return 'ladder-fill warning';
      return 'ladder-fill';
    };

    const ladder = rungs.map(rung => `
      <div class="ladder-rung">
        <div class="ladder-label">${rung.label}</div>
        <div class="ladder-bar-container">
          <div class="${getBarClass(rung.value)}" style="width: ${(rung.value / maxVal) * 100}%"></div>
        </div>
        <div class="ladder-value">${fmt.num(rung.value, 0)} μs</div>
      </div>
      <div class="ladder-explanation">${rung.desc}</div>
    `).join('');

    return `
      <div class="reliability-ladder">
        ${ladder}
        <div class="reliability-verdict">
          <span class="verdict-label">结论：</span>
          <span class="verdict-text">${verdict}</span>
        </div>
      </div>
    `;
  }

  private renderConfidence(data: ConfidenceResult): string {
    return `
      <div class="confidence-bar">
        <div class="confidence-fill" style="width:${data.score}%"></div>
        <div class="confidence-text">${fmt.num(data.score, 0)}%</div>
      </div>
      <div class="confidence-notes">
        ${data.issues.map(issue => `
          <div class="confidence-note">
            <span>${issue.icon}</span>
            <span>${issue.message}</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  /**
   * 渲染历史记录
   */
  renderMeasurementHistory(history: Array<{ timestamp: number; rate: number; samples: number }>): void {
    const section = $('historySection');
    const content = $('historyContent');
    if (!section || !content || history.length === 0) return;

    section.style.display = 'block';

    const best = Math.max(...history.map(h => h.rate));
    const avg = history.reduce((s, h) => s + h.rate, 0) / history.length;

    content.innerHTML = `
      <div class="history-grid">
        <div class="history-item">
          <div class="history-label">最高</div>
          <div class="history-value best">${fmt.int(Math.round(best))}</div>
        </div>
        <div class="history-item">
          <div class="history-label">平均</div>
          <div class="history-value">${fmt.int(Math.round(avg))}</div>
        </div>
      </div>
      <div class="history-list">
        ${history.map((h, i) => `
          <span class="history-tag ${h.rate === best ? 'is-best' : ''}">#${i + 1} ${fmt.int(Math.round(h.rate))}</span>
        `).join('')}
      </div>
    `;
  }

  /**
   * 隐藏分析结果
   */
  hideAnalysisCards(): void {
    $('resultView')?.classList.add('hidden');
    $('testingView')?.classList.add('hidden');
    $('welcomeView')?.classList.remove('hidden');

    const actionSection = $('actionSection');
    const historySection = $('historySection');
    const deviceInfo = $('deviceInfo');
    const noDevice = $('noDeviceText');

    if (actionSection) actionSection.style.display = 'none';
    if (historySection) historySection.style.display = 'none';
    if (deviceInfo) deviceInfo.style.display = 'none';
    if (noDevice) noDevice.style.display = 'block';
  }

  // 兼容旧调用
  updateCaptureCount(count: number): void {
    this.updateCaptureProgress(count, 0);
  }
}
